import React from 'react';
import {
	FieldValues,
	UseFormProps,
	UseFormReturn,
	useForm,
} from 'react-hook-form';

type IsFieldDirtyFn = (fieldPath: string) => boolean;

type ValueCleaner<T> = (payload: T) => T;

type SavePayloadBuilder<TFormValues extends FieldValues, TSavePayload> = (
	values: TFormValues,
	helpers: {
		isFieldDirty: IsFieldDirtyFn;
		getValues: UseFormReturn<TFormValues>['getValues'];
	}
) => TSavePayload | null | undefined;

type SubmitPayloadBuilder<
	TFormValues extends FieldValues,
	TSubmitPayload,
	TSubmitArgs
> = (values: TFormValues, args: TSubmitArgs) => TSubmitPayload;

type SaveHandler<TSavePayload, TSaveArgs> = (
	payload: TSavePayload,
	args: TSaveArgs
) => Promise<void> | void;

type SubmitHandler<TSubmitPayload, TSubmitArgs> = (
	payload: TSubmitPayload,
	args: TSubmitArgs
) => Promise<void> | void;

type Hydrator<TSource, TFormValues> = (source: TSource) => TFormValues;

export interface UseCrewFormConfig<
	TFormValues extends FieldValues,
	THydrateSource = unknown,
	TSavePayload = unknown,
	TSubmitPayload = unknown,
	TSaveArgs = void,
	TSubmitArgs = void
> {
	defaultValues: UseFormProps<TFormValues>['defaultValues'];
	mode?: UseFormProps<TFormValues>['mode'];
	reValidateMode?: UseFormProps<TFormValues>['reValidateMode'];
	hydrate?: Hydrator<THydrateSource, TFormValues>;
	buildSavePayload?: SavePayloadBuilder<TFormValues, TSavePayload>;
	onSave?: SaveHandler<TSavePayload, TSaveArgs>;
	cleanSavePayload?: ValueCleaner<TSavePayload>;
	buildSubmitPayload?: SubmitPayloadBuilder<
		TFormValues,
		TSubmitPayload,
		TSubmitArgs
	>;
	onSubmit?: SubmitHandler<TSubmitPayload, TSubmitArgs>;
	cleanSubmitPayload?: ValueCleaner<TSubmitPayload>;
	isFormValid?: (values: TFormValues, args: any) => boolean;
}

const defaultCleanPayload = <TPayload>(payload: TPayload): TPayload => {
	if (Array.isArray(payload)) {
		return payload
			.map((item) => defaultCleanPayload(item))
			.filter((item) => item !== undefined && item !== null) as TPayload;
	}

	if (payload && typeof payload === 'object') {
		const cleaned: Record<string, unknown> = {};
		Object.entries(payload as Record<string, unknown>).forEach(
			([key, value]) => {
				if (value === undefined || value === null) {
					return;
				}

				if (typeof value === 'string') {
					const trimmed = value.trim();
					if (trimmed.length === 0) {
						return;
					}
					cleaned[key] = trimmed;
					return;
				}

				if (Array.isArray(value)) {
					const nestedArray = value
						.map((item) => defaultCleanPayload(item))
						.filter((item) => item !== undefined && item !== null);
					if (nestedArray.length > 0 || value.length === 0) {
						cleaned[key] = nestedArray;
					}
					return;
				}

				if (typeof value === 'object') {
					const nested = defaultCleanPayload(value);
					if (
						nested &&
						typeof nested === 'object' &&
						Object.keys(nested as Record<string, unknown>)
							.length === 0
					) {
						return;
					}
					cleaned[key] = nested;
					return;
				}

				cleaned[key] = value;
			}
		);
		return cleaned as unknown as TPayload;
	}

	return payload;
};

export function useCrewForm<
	TFormValues extends FieldValues,
	THydrateSource = unknown,
	TSavePayload = unknown,
	TSubmitPayload = unknown,
	TSaveArgs = void,
	TSubmitArgs = void
>(
	config: UseCrewFormConfig<
		TFormValues,
		THydrateSource,
		TSavePayload,
		TSubmitPayload,
		TSaveArgs,
		TSubmitArgs
	>
) {
	const {
		defaultValues,
		mode,
		reValidateMode,
		hydrate,
		buildSavePayload,
		onSave,
		cleanSavePayload,
		buildSubmitPayload,
		onSubmit,
		cleanSubmitPayload,
		isFormValid,
	} = config;

	const form = useForm<TFormValues>({
		defaultValues,
		mode,
		reValidateMode,
	});

	const {
		control,
		formState: { errors, dirtyFields },
		setValue,
		watch,
		reset,
		trigger,
		getValues,
		handleSubmit,
	} = form;

	const formValues = watch();

	const isFieldDirty: IsFieldDirtyFn = React.useCallback(
		(fieldPath: string) => {
			const pathParts = fieldPath.split('.');
			let current: any = dirtyFields;

			for (const part of pathParts) {
				if (current && typeof current === 'object' && part in current) {
					current = current[part];
				} else {
					return false;
				}
			}

			return Boolean(current);
		},
		[dirtyFields]
	);

	const hydrateForm = React.useCallback(
		(source: THydrateSource | null | undefined) => {
			if (!hydrate || !source) {
				return;
			}
			const mapped = hydrate(source);
			reset(mapped, { keepDirty: false, keepTouched: false });
		},
		[hydrate, reset]
	);

	const saveProgress = React.useCallback(
		async (args?: TSaveArgs) => {
			if (!buildSavePayload || !onSave) {
				return true;
			}

			try {
				const payload = buildSavePayload(getValues(), {
					isFieldDirty,
					getValues,
				});
				if (
					!payload ||
					(typeof payload === 'object' &&
						Object.keys(payload as Record<string, unknown>)
							.length === 0)
				) {
					return true;
				}

				const cleanedPayload = cleanSavePayload
					? cleanSavePayload(payload)
					: defaultCleanPayload(payload);

				await onSave(cleanedPayload, args as TSaveArgs);
				return true;
			} catch (error) {
				console.error('Error saving crew form progress:', error);
				return false;
			}
		},
		[buildSavePayload, onSave, cleanSavePayload, getValues, isFieldDirty]
	);

	const submitForm = React.useCallback(
		async (args?: TSubmitArgs) => {
			if (!buildSubmitPayload || !onSubmit) {
				throw new Error(
					'Submit handler not configured for useCrewForm'
				);
			}

			const payload = buildSubmitPayload(
				getValues(),
				args as TSubmitArgs
			);
			const cleanedPayload = cleanSubmitPayload
				? cleanSubmitPayload(payload)
				: defaultCleanPayload(payload);

			await onSubmit(cleanedPayload, args as TSubmitArgs);
		},
		[buildSubmitPayload, onSubmit, cleanSubmitPayload, getValues]
	);

	const validateForm = React.useCallback(
		(args?: any) => {
			if (!isFormValid) {
				return true;
			}
			return isFormValid(getValues(), args);
		},
		[isFormValid, getValues]
	);

	return {
		control,
		errors,
		setValue,
		formValues,
		trigger,
		reset,
		getValues,
		handleSubmit,
		hydrateForm,
		saveProgress,
		submitForm,
		isFieldDirty,
		validateForm,
	};
}

export type CrewFormReturn<TFormValues extends FieldValues> = ReturnType<
	typeof useCrewForm<TFormValues>
>;
